/**
 * HTML Export Template for CVE Attack Path Visualization
 */

const HTML_STYLES = `
* { box-sizing: border-box; margin: 0; padding: 0; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
  color: #c9d1d9;
  min-height: 100vh;
}
.header {
  background: linear-gradient(135deg, #1a2332 0%, #0d1117 100%);
  padding: 40px;
  border-bottom: 1px solid #30363d;
}
.header h1 {
  font-size: 32px;
  color: #fff;
  margin-bottom: 8px;
  font-weight: 700;
}
.header .subtitle {
  color: #8b949e;
  font-size: 16px;
  margin-bottom: 24px;
}
.stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
.stat {
  background: rgba(255,255,255,0.03);
  padding: 16px 24px;
  border-radius: 12px;
  border: 1px solid #30363d;
  min-width: 140px;
}
.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #58a6ff;
  line-height: 1;
}
.stat-label {
  font-size: 13px;
  color: #8b949e;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.visualization-section {
  padding: 40px;
}
.section-title {
  font-size: 20px;
  color: #fff;
  margin-bottom: 20px;
  font-weight: 600;
}
.visualization {
  background: #1a2332;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #30363d;
  padding: 20px;
}
.visualization svg {
  display: block;
  width: 100%;
  height: auto;
}
.cve-section {
  padding: 0 40px 40px;
}
.cve-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}
.cve-card {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}
.cve-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}
.cve-card__header {
  background: linear-gradient(135deg, #21262d 0%, #161b22 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #30363d;
}
.cve-id {
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  font-weight: 600;
  font-size: 15px;
  color: #f85149;
}
.cvss-badge {
  background: linear-gradient(135deg, #f85149 0%, #da3633 100%);
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}
.cve-card__body {
  padding: 20px;
}
.cve-field {
  font-size: 14px;
  color: #8b949e;
  margin-bottom: 12px;
  line-height: 1.6;
}
.cve-field:last-child {
  margin-bottom: 0;
}
.field-label {
  color: #c9d1d9;
  font-weight: 500;
}
.techniques {
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  color: #58a6ff;
  font-size: 13px;
}
.footer {
  padding: 24px 40px;
  border-top: 1px solid #30363d;
  text-align: center;
  color: #6e7681;
  font-size: 13px;
  background: #0d1117;
}
.json-section {
  padding: 0 40px 40px;
}
.json-toggle {
  background: #21262d;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 12px 20px;
  color: #58a6ff;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.json-toggle:hover {
  background: #30363d;
}
.json-content {
  display: none;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 12px;
  padding: 20px;
  overflow-x: auto;
}
.json-content.visible {
  display: block;
}
.json-content pre {
  color: #8b949e;
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
`;

/**
 * Build CVE card HTML
 */
function buildCveCard(path) {
  return `
    <div class="cve-card">
      <div class="cve-card__header">
        <span class="cve-id">${path.cve}</span>
        ${path.cvss ? `<span class="cvss-badge">${path.cvss}</span>` : ''}
      </div>
      <div class="cve-card__body">
        <div class="cve-field"><span class="field-label">Attack Vector:</span> ${path.attackVector || 'N/A'}</div>
        <div class="cve-field"><span class="field-label">Techniques:</span> <span class="techniques">${(path.techniques || []).join(', ') || 'N/A'}</span></div>
        ${path.potentialRisk ? `<div class="cve-field"><span class="field-label">Risk:</span> ${path.potentialRisk}</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Generate full HTML export document
 */
export function generateHtmlExport(data, svgContent = '') {
  const cveCards = data.attackPaths.map(buildCveCard).join('');
  const techniqueCount = data.nodes.filter(n => n.name.startsWith('T')).length;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>CVE Attack Path Visualization - ${data.cves.length} CVEs</title>
  <style>${HTML_STYLES}</style>
</head>
<body>
  <div class="header">
    <h1>CVE Attack Path Visualization</h1>
    <p class="subtitle">CAPEC → MITRE ATT&CK Technique Mapping</p>
    <div class="stats">
      <div class="stat">
        <div class="stat-value">${data.cves.length}</div>
        <div class="stat-label">CVEs Analyzed</div>
      </div>
      <div class="stat">
        <div class="stat-value">${techniqueCount}</div>
        <div class="stat-label">Techniques</div>
      </div>
      <div class="stat">
        <div class="stat-value">${data.links.length}</div>
        <div class="stat-label">Attack Paths</div>
      </div>
    </div>
  </div>

  <div class="visualization-section">
    <h2 class="section-title">Attack Flow Visualization</h2>
    <div class="visualization">${svgContent || '<p style="padding: 40px; text-align: center; color: #6e7681;">Visualization not available</p>'}</div>
  </div>

  <div class="cve-section">
    <h2 class="section-title">CVE Details</h2>
    <div class="cve-grid">${cveCards || '<p style="color: #6e7681;">No CVE details available</p>'}</div>
  </div>

  <div class="json-section">
    <button class="json-toggle" onclick="document.getElementById('json-data').classList.toggle('visible')">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm0 1v12h8V2H4z"/></svg>
      Toggle Raw JSON Data
    </button>
    <div id="json-data" class="json-content">
      <pre>${JSON.stringify(data, null, 2)}</pre>
    </div>
  </div>

  <div class="footer">
    Generated on ${new Date().toLocaleString()} | CVE Attack Path Visualization Report
  </div>
</body>
</html>`;
}

/**
 * Prepare SVG for export (clone and fix for standalone display)
 */
export function prepareSvgForExport(svgElement) {
  if (!svgElement) return '';

  const clone = svgElement.cloneNode(true);
  clone.setAttribute('width', '100%');
  clone.setAttribute('height', '500');
  clone.setAttribute('viewBox', `0 0 ${svgElement.clientWidth || 800} ${svgElement.clientHeight || 500}`);
  clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Fix text colors for dark background
  clone.querySelectorAll('text').forEach(text => {
    text.setAttribute('fill', '#c9d1d9');
  });

  return clone.outerHTML;
}

/**
 * Download file helper
 */
export function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
